import { OSU_PLAYFIELD_HEIGHT, OSU_PLAYFIELD_WIDTH } from '@shared/circlesChart';

/**
 * Shared playfield geometry used by both the Circles renderer and the
 * Circles input source. Keeping the math (and the minimum-size floor)
 * in one place stops the two layers from diverging — at sub-floor CSS
 * sizes (a portrait phone, devtools narrow viewport, embedded preview)
 * a divergence makes taps land on a different osu coordinate from the
 * circle the player sees, and the playfield silently un-aligns.
 *
 * The floor matches what the renderer pretends the canvas is (it
 * scales DPR-aware); the input source clamps the same way so screen
 * → osu mapping reads the same view dimensions.
 */
export const MIN_CANVAS_VIEW_WIDTH = 320;
export const MIN_CANVAS_VIEW_HEIGHT = 180;

export interface PlayfieldRect {
  /** Top-left x of the osu playfield within the canvas's CSS view. */
  x: number;
  /** Top-left y of the osu playfield within the canvas's CSS view. */
  y: number;
  /** Width of the osu playfield within the canvas's CSS view. */
  w: number;
  /** Height of the osu playfield within the canvas's CSS view. */
  h: number;
  /** CSS px per osu unit (`w / OSU_PLAYFIELD_WIDTH`). */
  scale: number;
}

/**
 * Letterbox the 4:3 osu playfield inside a CSS view of `(viewWidth,
 * viewHeight)`. Either left/right bars (wide canvas) or top/bottom
 * bars (tall canvas) — never both.
 */
export function fitPlayfield(viewWidth: number, viewHeight: number): PlayfieldRect {
  const aspect = OSU_PLAYFIELD_WIDTH / OSU_PLAYFIELD_HEIGHT;
  let w = viewWidth;
  let h = viewWidth / aspect;
  if (h > viewHeight) {
    h = viewHeight;
    w = viewHeight * aspect;
  }
  const x = (viewWidth - w) / 2;
  const y = (viewHeight - h) / 2;
  return { x, y, w, h, scale: w / OSU_PLAYFIELD_WIDTH };
}

/**
 * Compute the canvas's CSS view dimensions, applying the same minimum
 * floor that the renderer does. The renderer paints into a backing
 * store sized at this floored view × DPR; the input source must read
 * coordinates against the same floored view to stay in sync.
 *
 * Returns floored integers so renderer and input source see the exact
 * same numbers (avoiding sub-pixel drift between two rounding paths).
 */
export function computeCanvasView(canvas: HTMLCanvasElement): {
  rect: DOMRect;
  viewWidth: number;
  viewHeight: number;
} {
  const rect = canvas.getBoundingClientRect();
  const viewWidth = Math.max(MIN_CANVAS_VIEW_WIDTH, Math.floor(rect.width));
  const viewHeight = Math.max(MIN_CANVAS_VIEW_HEIGHT, Math.floor(rect.height));
  return { rect, viewWidth, viewHeight };
}

/**
 * Compute the playfield rect in CSS-pixel space relative to the
 * canvas's bounding-box (rect.left/top). Caller adds `rect.left/top`
 * to the rect's `x`/`y` to get window-relative coordinates.
 */
export function computeCanvasPlayfield(canvas: HTMLCanvasElement): {
  rect: DOMRect;
  viewWidth: number;
  viewHeight: number;
  playfield: PlayfieldRect;
} {
  const { rect, viewWidth, viewHeight } = computeCanvasView(canvas);
  return {
    rect,
    viewWidth,
    viewHeight,
    playfield: fitPlayfield(viewWidth, viewHeight),
  };
}
